import { ReactElement, ReactNode } from 'react';
import {
	Alert, Box, Chip, Paper, Tooltip, Typography, useMediaQuery, useTheme, alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Util } from '../../util/Util';
import {
	BarChart, Bar, XAxis, YAxis, CartesianGrid, ComposedChart, Line,
	Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';

import { roundMoney } from "../../util/quantity";
export const RECON_CHART_COLORS = {
	invoiced: '#0288d1',
	paid: '#2e7d32',
	balance: '#ed6c02',
	agent: '#d32f2f',
	client: '#f57c00',
	overlap: '#1565c0',
};

export type ReconPhase = 'before' | 'period' | 'end';

export type ReconPhaseData = { invoiced: number; paid: number; balance: number };

export type ReconRow = {
	label: string;
	before: ReconPhaseData;
	period: ReconPhaseData;
	end: ReconPhaseData;
};

export type WaterfallStep = {
	name: string;
	base: number;
	value: number;
	display: number;
	fill: string;
	isDelta?: boolean;
};

export type EntityChartPoint = {
	entity: string;
	invoiced: number;
	paid: number;
	balance: number;
};

export type BalanceChartPoint = {
	entity: string;
	balance: number;
	fill: string;
};

export type SituationKpi = {
	label: string;
	value: number;
	signed: boolean;
};

export const formatChartValue = (value: number) => {
	if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
	if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
	return value.toFixed(0);
};

export const formatChartTooltip = (value: number) => Util.formatNumber(Number(value));

export const isUnlimitedCredit = (limit: number) => limit === -1;

export const getChartSurface = (theme: Theme) => ({
	grid: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.45 : 1),
	axis: theme.palette.text.disabled,
	tick: theme.palette.text.secondary,
	legend: theme.palette.text.primary,
	panelBg: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50],
	stripeBg: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : theme.palette.grey[50],
	cardShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
	creditBalance: theme.palette.success.main,
	debitBalance: theme.palette.error.main,
});

export const getRechartsTooltipProps = (theme: Theme) => ({
	contentStyle: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		color: theme.palette.text.primary,
		boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 8 : 2],
	},
	labelStyle: { color: theme.palette.text.secondary },
	itemStyle: { color: theme.palette.text.primary },
});

export const getUsageGradient = (percent: number, theme: Theme) => {
	if (percent <= 60) {
		return `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`;
	}
	if (percent <= 80) {
		return `linear-gradient(90deg, ${theme.palette.success.light} 0%, ${theme.palette.warning.main} 100%)`;
	}
	return `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`;
};

export const buildWaterfallSteps = (
	row: ReconRow,
	colors: { balance: string; increase: string; decrease: string },
): WaterfallStep[] => {
	const beforeBalance = row.before.balance;
	const periodDelta = row.period.balance;
	const endBalance = row.end.balance;

	return [
		{ name: 'Before', base: 0, value: beforeBalance, display: beforeBalance, fill: colors.balance },
		{
			name: 'Within period',
			base: periodDelta >= 0 ? beforeBalance : beforeBalance + periodDelta,
			value: Math.abs(periodDelta),
			display: periodDelta,
			fill: periodDelta >= 0 ? colors.increase : colors.decrease,
			isDelta: true,
		},
		{ name: 'End of period', base: 0, value: endBalance, display: endBalance, fill: colors.balance },
	];
};

export const formatDetailValue = (value: unknown) => {
	if (value === null || value === undefined || value === '') {
		return '-';
	}

	const numberValue = Number(value);
	if (!Number.isNaN(numberValue) && String(value).trim() !== '') {
		return Util.formatNumber(numberValue);
	}

	return String(value);
};

export const renderBalanceValue = (value: number, theme: Theme) => {
	const color = value < 0
		? 'success.main'
		: value > 0
			? (theme.palette.mode === 'dark' ? 'warning.light' : 'warning.main')
			: 'text.primary';
	return (
		<Box component="span" sx={{ fontWeight: 600, color }}>
			{formatDetailValue(value)}
		</Box>
	);
};

const chartCardSx = (chartSurface: ReturnType<typeof getChartSurface>) => ({
	p: { xs: 1.25, md: 2.5 },
	borderRadius: 2,
	border: '1px solid',
	borderColor: 'divider',
	bgcolor: 'background.paper',
	boxShadow: chartSurface.cardShadow,
});

const chartTitleSx = {
	fontWeight: 700,
	mb: { xs: 1, md: 1.5 },
	fontSize: { xs: '0.8rem', md: '0.875rem' },
	lineHeight: 1.35,
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

type SituationDetailHeaderProps = {
	name: string;
	subtitle: string;
	isActive: boolean;
	unlimited: boolean;
	creditLimit: unknown;
	t: TranslateFn;
};

export const SituationDetailHeader = ({
	name, subtitle, isActive, unlimited, creditLimit, t,
}: SituationDetailHeaderProps) => (
	<Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
		<Box sx={{
			display: 'flex',
			flexDirection: { xs: 'column', sm: 'row' },
			flexWrap: 'wrap',
			gap: 1.5,
			justifyContent: 'space-between',
			alignItems: { xs: 'stretch', sm: 'center' },
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
				<Box
					sx={{
						width: { xs: 40, md: 44 },
						height: { xs: 40, md: 44 },
						borderRadius: '50%',
						bgcolor: 'primary.main',
						color: 'primary.contrastText',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontWeight: 700,
						fontSize: { xs: 16, md: 18 },
						flexShrink: 0,
					}}
				>
					{String(name).charAt(0).toUpperCase()}
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '1rem', md: '1.25rem' }, wordBreak: 'break-word' }}>
						{name}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
						{subtitle}
					</Typography>
				</Box>
			</Box>
			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
				<Chip
					label={isActive ? t('common:status.active') : t('common:status.inactive')}
					size="small"
					color={isActive ? 'success' : 'default'}
					variant="outlined"
					sx={{ flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
				/>
				<Chip
					label={unlimited ? `∞ ${t('common:pos.unlimited')}` : `${t('common:clientSituation.detailPanel.creditLimit')}: ${formatDetailValue(creditLimit)}`}
					size="small"
					color={unlimited ? 'primary' : 'default'}
					variant="outlined"
					sx={{
						flex: { xs: '1 1 100%', sm: '0 0 auto' },
						maxWidth: '100%',
						height: 'auto',
						'& .MuiChip-label': {
							whiteSpace: 'normal',
							lineHeight: 1.3,
							py: 0.5,
						},
					}}
				/>
			</Box>
		</Box>
	</Paper>
);

type SituationKpiGridProps = {
	stats: SituationKpi[];
	theme: Theme;
};

export const SituationKpiGrid = ({ stats, theme }: SituationKpiGridProps) => (
	<Box sx={{
		display: 'grid',
		gridTemplateColumns: { xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
		gap: { xs: 1, md: 1.5 },
		mb: 2,
	}}>
		{stats.map((stat) => (
			<Box
				key={stat.label}
				sx={{
					p: { xs: 1.25, md: 1.5 },
					borderRadius: 2,
					border: '1px solid',
					borderColor: 'divider',
					bgcolor: 'background.paper',
					minWidth: 0,
				}}
			>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: { xs: 9, md: 10 }, lineHeight: 1.3 }}>
					{stat.label}
				</Typography>
				<Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: { xs: '0.9rem', md: '1rem' }, wordBreak: 'break-word' }}>
					{stat.signed ? renderBalanceValue(stat.value, theme) : formatDetailValue(stat.value)}
				</Typography>
			</Box>
		))}
	</Box>
);

type SituationReconciliationProps = {
	rows: ReconRow[];
	periodActive: boolean;
	getRowLabel: (row: ReconRow) => string;
	isHighlightedRow?: (row: ReconRow) => boolean;
	subtitle?: ReactNode;
	t: TranslateFn;
	theme: Theme;
	i18nNamespace?: string;
};

export const SituationReconciliation = ({
	rows, periodActive, getRowLabel, isHighlightedRow, subtitle, t, theme,
	i18nNamespace = 'common:clientSituation.detailPanel',
}: SituationReconciliationProps) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const chartSurface = getChartSurface(theme);
	const phaseLabel = (phase: ReconPhase) => t(`${i18nNamespace}.phases.${phase}`);
	const fieldLabel = (field: keyof ReconPhaseData) => t(`${i18nNamespace}.fields.${field}`);

	const renderMobileCards = () => (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: { xs: 1.25, sm: 1.5 } }}>
			{rows.map((rowItem, index) => {
				const highlighted = isHighlightedRow?.(rowItem) ?? false;
				return (
					<Paper
						key={rowItem.label}
						elevation={0}
						sx={{
							p: 1.25,
							borderRadius: 2,
							border: '1px solid',
							borderColor: highlighted ? 'primary.main' : 'divider',
							bgcolor: highlighted ? 'action.selected' : index % 2 === 0 ? 'background.paper' : chartSurface.stripeBg,
						}}
					>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 700, color: highlighted ? 'primary.main' : 'text.primary', mb: 1 }}
						>
							{getRowLabel(rowItem)}
						</Typography>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							{(['before', 'period', 'end'] as ReconPhase[]).map((phase) => (
								<Box
									key={`${rowItem.label}-${phase}`}
									sx={{
										p: 1,
										borderRadius: 1.5,
										bgcolor: phase === 'period' && periodActive
											? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)
											: 'action.hover',
										border: '1px solid',
										borderColor: phase === 'period' && periodActive ? alpha(theme.palette.primary.main, 0.35) : 'divider',
									}}
								>
									<Typography
										variant="caption"
										sx={{
											display: 'block',
											mb: 0.75,
											fontWeight: 700,
											textTransform: 'uppercase',
											letterSpacing: 0.3,
											fontSize: 10,
											color: phase === 'period' && periodActive ? 'primary.main' : 'text.secondary',
										}}
									>
										{phaseLabel(phase)}
									</Typography>
									<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}>
										{(['invoiced', 'paid', 'balance'] as const).map((field) => {
											const value = rowItem[phase][field];
											return (
												<Box key={`${rowItem.label}-${phase}-${field}`} sx={{ minWidth: 0, textAlign: 'center' }}>
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9, mb: 0.25, lineHeight: 1.2 }}>
														{fieldLabel(field)}
													</Typography>
													<Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: { xs: '0.75rem', sm: '0.8125rem' }, fontWeight: field === 'balance' ? 600 : 500, wordBreak: 'break-word' }}>
														{field === 'balance' ? renderBalanceValue(value, theme) : formatDetailValue(value)}
													</Typography>
												</Box>
											);
										})}
									</Box>
								</Box>
							))}
						</Box>
					</Paper>
				);
			})}
		</Box>
	);

	const renderDesktopTable = () => (
		<Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
			<Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
				<Box component="thead">
					<Box component="tr" sx={{ bgcolor: 'background.default' }}>
						<Box component="th" sx={{ px: 2, py: 1.25, textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }} rowSpan={2}>
							{t('common:clientSituation.detailPanel.fields.category')}
						</Box>
						<Box component="th" sx={{ px: 2, py: 1, textAlign: 'center', fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase' }} colSpan={3}>
							{phaseLabel('before')}
						</Box>
						<Box component="th" sx={{ px: 2, py: 1, textAlign: 'center', fontWeight: 700, fontSize: 11, color: periodActive ? 'primary.main' : 'text.secondary', textTransform: 'uppercase' }} colSpan={3}>
							{phaseLabel('period')}
						</Box>
						<Box component="th" sx={{ px: 2, py: 1, textAlign: 'center', fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase' }} colSpan={3}>
							{phaseLabel('end')}
						</Box>
					</Box>
					<Box component="tr" sx={{ bgcolor: 'background.default' }}>
						{(['before', 'period', 'end'] as ReconPhase[]).flatMap((phase) => (
							(['invoiced', 'paid', 'balance'] as const).map((field) => (
								<Box
									key={`${phase}-${field}`}
									component="th"
									sx={{
										px: 2,
										py: 0.75,
										textAlign: 'right',
										fontWeight: 600,
										fontSize: 10,
										color: phase === 'period' && periodActive ? 'primary.main' : 'text.secondary',
									}}
								>
									{fieldLabel(field)}
								</Box>
							))
						))}
					</Box>
				</Box>
				<Box component="tbody">
					{rows.map((rowItem, index) => {
						const highlighted = isHighlightedRow?.(rowItem) ?? false;
						return (
							<Box
								component="tr"
								key={rowItem.label}
								sx={{
									borderTop: '1px solid',
									borderColor: 'divider',
									bgcolor: highlighted ? 'action.selected' : index % 2 === 0 ? 'background.paper' : chartSurface.stripeBg,
								}}
							>
								<Box component="td" sx={{ px: 2, py: 1.25, fontWeight: highlighted ? 700 : 600, color: highlighted ? 'primary.main' : 'text.primary' }}>
									{getRowLabel(rowItem)}
								</Box>
								{(['before', 'period', 'end'] as ReconPhase[]).flatMap((phase) => (
									(['invoiced', 'paid', 'balance'] as const).map((field) => {
										const value = rowItem[phase][field];
										return (
											<Box key={`${rowItem.label}-${phase}-${field}`} component="td" sx={{ px: 2, py: 1.25, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
												{field === 'balance' ? renderBalanceValue(value, theme) : formatDetailValue(value)}
											</Box>
										);
									})
								))}
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);

	return (
		<Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 2, bgcolor: 'background.paper' }}>
			<Box sx={{ px: { xs: 1.25, md: 2 }, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
					{t('common:clientSituation.detailPanel.reconciliation')}
				</Typography>
				{subtitle && (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.4 }}>
						{subtitle}
					</Typography>
				)}
			</Box>
			{isMobile ? renderMobileCards() : renderDesktopTable()}
		</Paper>
	);
};

type SituationEntityComposedChartProps = {
	data: EntityChartPoint[];
	t: TranslateFn;
	theme: Theme;
	dotRenderer?: (props: any) => ReactElement<SVGElement>;
	i18nNamespace?: string;
};

export const SituationEntityComposedChart = ({
	data, t, theme, dotRenderer, i18nNamespace = 'common:clientSituation.detailPanel',
}: SituationEntityComposedChartProps) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isCompact = useMediaQuery(theme.breakpoints.down('md'));
	const chartSurface = getChartSurface(theme);
	const rechartsTooltipProps = getRechartsTooltipProps(theme);
	const tickSize = isMobile ? 9 : isCompact ? 10 : 11;
	const axisTickProps = { fontSize: tickSize, fill: chartSurface.tick };
	const chartHeight = isMobile ? 300 : isCompact ? 260 : 240;

	return (
		<ResponsiveContainer width="100%" height={chartHeight}>
			<ComposedChart
				data={data}
				margin={{
					top: 8,
					right: isMobile ? 4 : 12,
					left: isMobile ? 0 : 4,
					bottom: isMobile ? 52 : isCompact ? 28 : 0,
				}}
			>
				<CartesianGrid strokeDasharray="3 3" stroke={chartSurface.grid} vertical={false} />
				<XAxis
					dataKey="entity"
					tick={axisTickProps}
					stroke={chartSurface.axis}
					interval={0}
					angle={isMobile ? -35 : isCompact ? -20 : 0}
					textAnchor={isMobile || isCompact ? 'end' : 'middle'}
					height={isMobile ? 56 : isCompact ? 40 : 30}
				/>
				<YAxis tickFormatter={formatChartValue} tick={{ fontSize: tickSize - 1, fill: chartSurface.tick }} stroke={chartSurface.axis} width={isMobile ? 40 : 48} />
				<RechartsTooltip {...rechartsTooltipProps} formatter={(value: number, name: string) => [formatChartTooltip(value), name]} />
				<Legend
					wrapperStyle={{
						fontSize: isMobile ? 9 : 11,
						paddingTop: 8,
						color: chartSurface.legend,
					}}
					verticalAlign="bottom"
				/>
				<Bar dataKey="invoiced" name={t(`${i18nNamespace}.fields.invoiced`)} fill={RECON_CHART_COLORS.invoiced} radius={[4, 4, 0, 0]} barSize={isMobile ? 10 : isCompact ? 14 : 16} />
				<Bar dataKey="paid" name={t(`${i18nNamespace}.fields.paid`)} fill={RECON_CHART_COLORS.paid} radius={[4, 4, 0, 0]} barSize={isMobile ? 10 : isCompact ? 14 : 16} />
				<Line
					type="monotone"
					dataKey="balance"
					name={t(`${i18nNamespace}.fields.balance`)}
					stroke={RECON_CHART_COLORS.balance}
					strokeWidth={isMobile ? 2 : 2.5}
					dot={dotRenderer ?? { r: isMobile ? 4 : 5, fill: RECON_CHART_COLORS.balance }}
				/>
			</ComposedChart>
		</ResponsiveContainer>
	);
};

type SituationEndBalancesChartProps = {
	data: BalanceChartPoint[];
	t: TranslateFn;
	theme: Theme;
};

export const SituationEndBalancesChart = ({ data, t, theme }: SituationEndBalancesChartProps) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const chartSurface = getChartSurface(theme);
	const rechartsTooltipProps = getRechartsTooltipProps(theme);
	const tickSize = isMobile ? 9 : 10;
	const chartHeight = Math.max(isMobile ? 200 : 220, data.length * (isMobile ? 48 : 40));

	return (
		<ResponsiveContainer width="100%" height={chartHeight}>
			<BarChart layout="vertical" data={data} margin={{ top: 4, right: isMobile ? 8 : 16, left: 4, bottom: 4 }}>
				<CartesianGrid strokeDasharray="3 3" stroke={chartSurface.grid} horizontal={false} />
				<XAxis type="number" tickFormatter={formatChartValue} tick={{ fontSize: tickSize, fill: chartSurface.tick }} stroke={chartSurface.axis} />
				<YAxis
					type="category"
					dataKey="entity"
					tick={{ fontSize: tickSize, fill: chartSurface.tick }}
					stroke={chartSurface.axis}
					width={isMobile ? 64 : 88}
				/>
				<RechartsTooltip {...rechartsTooltipProps} formatter={(value: number) => formatChartTooltip(value)} />
				<Bar dataKey="balance" name={t('common:clientSituation.detailPanel.fields.balance')} radius={[0, 4, 4, 0]} barSize={isMobile ? 14 : 18}>
					{data.map((entry) => (
						<Cell key={entry.entity} fill={entry.fill} />
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

type SituationBalanceFlowChartProps = {
	data: WaterfallStep[];
	t: TranslateFn;
	theme: Theme;
};

export const SituationBalanceFlowChart = ({ data, t, theme }: SituationBalanceFlowChartProps) => {
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const chartSurface = getChartSurface(theme);
	const tickSize = isMobile ? 9 : 10;

	return (
		<ResponsiveContainer width="100%" height={isMobile ? 260 : 220}>
			<BarChart data={data} margin={{ top: 8, right: isMobile ? 4 : 12, left: isMobile ? 0 : 4, bottom: isMobile ? 8 : 0 }}>
				<CartesianGrid strokeDasharray="3 3" stroke={chartSurface.grid} vertical={false} />
				<XAxis
					dataKey="name"
					tick={{ fontSize: tickSize, fill: chartSurface.tick }}
					stroke={chartSurface.axis}
					interval={0}
					angle={isMobile ? -25 : 0}
					textAnchor={isMobile ? 'end' : 'middle'}
					height={isMobile ? 64 : 30}
				/>
				<YAxis tickFormatter={formatChartValue} tick={{ fontSize: tickSize, fill: chartSurface.tick }} stroke={chartSurface.axis} width={isMobile ? 40 : 48} />
				<RechartsTooltip
					content={({ active, payload }) => {
						if (!active || !payload?.length) return null;
						const item = payload[0]?.payload as WaterfallStep | undefined;
						if (!item) return null;
						return (
							<Box sx={{
								bgcolor: 'background.paper',
								color: 'text.primary',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1,
								p: 1,
								boxShadow: chartSurface.cardShadow === 'none' ? theme.shadows[8] : theme.shadows[2],
							}}>
								<Typography variant="caption" sx={{ fontWeight: 600, display: 'block', color: 'text.primary' }}>
									{item.name}
								</Typography>
								<Typography variant="caption" sx={{ color: 'text.secondary' }}>
									{item.isDelta ? `${t('common:clientSituation.detailPanel.periodChange')}: ` : `${t('common:clientSituation.detailPanel.fields.balance')}: `}
									{formatChartTooltip(item.display)}
								</Typography>
							</Box>
						);
					}}
				/>
				<Bar dataKey="base" stackId="waterfall" fill="transparent" />
				<Bar dataKey="value" stackId="waterfall" radius={[4, 4, 0, 0]}>
					{data.map((entry) => (
						<Cell key={entry.name} fill={entry.fill} />
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

type SituationChartCardProps = {
	title: string;
	children: ReactNode;
	chartSurface: ReturnType<typeof getChartSurface>;
};

export const SituationChartCard = ({ title, children, chartSurface }: SituationChartCardProps) => (
	<Box sx={chartCardSx(chartSurface)}>
		<Typography variant="subtitle2" sx={chartTitleSx}>
			{title}
		</Typography>
		{children}
	</Box>
);

export const SituationDetailShell = ({ children, theme }: { children: ReactNode; theme: Theme }) => {
	const chartSurface = getChartSurface(theme);

	return (
		<Box sx={{ p: { xs: 1, sm: 1.5, md: 2.5 }, bgcolor: chartSurface.panelBg }}>
			{children}
		</Box>
	);
};

export const SituationPeriodAlert = ({ periodActive, label }: { periodActive: boolean; label: string }) => {
	if (periodActive) return null;

	return (
		<Alert severity="info" sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: { xs: '0.8rem', md: '0.875rem' } } }}>
			{label}
		</Alert>
	);
};

export const SituationChartsGrid = ({
	overviewTitle,
	overviewChart,
	periodActive,
	periodTitle,
	periodChart,
	endBalanceTitle,
	endBalanceChart,
	balanceFlowTitle,
	balanceFlowChart,
	chartSurface,
}: {
	overviewTitle: string;
	overviewChart: ReactNode;
	periodActive: boolean;
	periodTitle: string;
	periodChart: ReactNode;
	endBalanceTitle: string;
	endBalanceChart: ReactNode;
	balanceFlowTitle: string;
	balanceFlowChart: ReactNode;
	chartSurface: ReturnType<typeof getChartSurface>;
}) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
		<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: periodActive ? '1fr 1fr' : '1fr' }, gap: { xs: 1.5, md: 2 } }}>
			<SituationChartCard title={overviewTitle} chartSurface={chartSurface}>
				{overviewChart}
			</SituationChartCard>
			{periodActive && (
				<SituationChartCard title={periodTitle} chartSurface={chartSurface}>
					{periodChart}
				</SituationChartCard>
			)}
		</Box>
		<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 1.5, md: 2 } }}>
			<SituationChartCard title={endBalanceTitle} chartSurface={chartSurface}>
				{endBalanceChart}
			</SituationChartCard>
			<SituationChartCard title={balanceFlowTitle} chartSurface={chartSurface}>
				{balanceFlowChart}
			</SituationChartCard>
		</Box>
	</Box>
);

export const creditUsageTableCellProps = {
	muiTableHeadCellProps: {
		align: 'center' as const,
		sx: { textAlign: 'center' },
	},
	muiTableBodyCellProps: {
		align: 'center' as const,
		sx: {
			textAlign: 'center',
			verticalAlign: 'middle',
			px: { xs: 1, sm: 1.5, md: 2 },
		},
	},
};

const creditUsageCellWrapperSx = {
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	py: 0.25,
};

const creditUsageContentSx = {
	width: '100%',
	maxWidth: 360,
	mx: 'auto',
	display: 'inline-flex',
	flexDirection: 'column',
	gap: 0.75,
};

type CreditUsageCellProps = {
	balance: number;
	creditLimit: number;
	t: TranslateFn;
	theme: Theme;
};

export const CreditUsageCell = ({ balance, creditLimit, t, theme }: CreditUsageCellProps) => {
	const isUnlimited = isUnlimitedCredit(creditLimit);
	const remaining = Math.max(0, roundMoney(creditLimit - balance));
	const usedPercent = creditLimit > 0
		? Math.min(100, Math.round((balance / creditLimit) * 100))
		: 0;
	const remainingPercent = creditLimit > 0 ? Math.max(0, 100 - usedPercent) : 0;

	const creditUsageNote = (
		<Typography variant="caption" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
			{t('common:clientSituation.creditUsageNote')}
		</Typography>
	);

	const remainingColor = remainingPercent < 15
		? 'error.main'
		: remainingPercent < 35
			? 'warning.main'
			: 'success.main';

	if (isUnlimited) {
		return (
			<Box sx={creditUsageCellWrapperSx}>
				<Tooltip title={creditUsageNote} arrow>
					<Box
						component="span"
						sx={{
							...creditUsageContentSx,
							alignItems: 'center',
						}}
					>
						<Chip
							label={`∞ ${t('common:pos.unlimited')}`}
							size="small"
							variant="outlined"
							color="primary"
							sx={{ fontWeight: 700, letterSpacing: 0.5 }}
						/>
						<Typography variant="caption" component="div" sx={{ textAlign: 'center', lineHeight: 1.5, width: '100%' }}>
							<Box component="span" color="text.secondary">{t('common:clientSituation.used')} </Box>
							<Box component="span" fontWeight={600} color="text.primary">{Util.formatNumber(balance)}</Box>
							<Box component="span" color="text.secondary">.</Box>
						</Typography>
					</Box>
				</Tooltip>
			</Box>
		);
	}

	return (
		<Box sx={creditUsageCellWrapperSx}>
			<Tooltip title={creditUsageNote} arrow>
				<Box
					component="span"
					sx={{
						...creditUsageContentSx,
						alignItems: 'stretch',
					}}
				>
					<Box sx={{ position: 'relative', height: 18, borderRadius: 6, bgcolor: 'action.hover', overflow: 'hidden', width: '100%' }}>
						<Box
							sx={{
								position: 'absolute',
								left: 0,
								top: 0,
								height: '100%',
								width: `${usedPercent}%`,
								background: getUsageGradient(usedPercent, theme),
								transition: 'width 0.4s ease-in-out',
								borderRadius: 6,
							}}
						/>
						<Typography
							variant="caption"
							sx={{
								position: 'absolute',
								inset: 0,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								fontSize: '10px',
								fontWeight: 700,
								color: usedPercent >= 40 ? 'common.white' : 'text.primary',
								textShadow: usedPercent >= 40 ? `0 1px 2px ${alpha(theme.palette.common.black, 0.45)}` : 'none',
								pointerEvents: 'none',
							}}
						>
							{usedPercent}% {t('common:clientSituation.usedLower')}
						</Typography>
					</Box>

					<Typography
						variant="caption"
						component="div"
						sx={{ textAlign: 'center', lineHeight: 1.5, letterSpacing: 0.1, width: '100%' }}
					>
						<Box component="span" color="text.secondary">{t('common:clientSituation.used')} </Box>
						<Box component="span" fontWeight={600} color="text.primary">{Util.formatNumber(balance)}</Box>
						<Box component="span" color="text.secondary"> | </Box>
						<Box component="span" fontWeight={600} color="text.primary">{Util.formatNumber(remaining)}</Box>
						<Box component="span" color="text.secondary"> (</Box>
						<Box component="span" fontWeight={700} color={remainingColor}>{remainingPercent}%</Box>
						<Box component="span" color="text.secondary">) {t('common:clientSituation.of')} </Box>
						<Box component="span" fontWeight={600} color="text.primary">{Util.formatNumber(creditLimit)}</Box>
						<Box component="span" color="text.secondary"> {t('common:clientSituation.remaining')}.</Box>
					</Typography>
				</Box>
			</Tooltip>
		</Box>
	);
};